/**
 * useFeatureSelection.ts
 * Feature selection logic for map interactions
 */

import { useDispatch } from "react-redux";

import { Map } from "maplibre-gl";

import {
  CATCHMENT_ID_ATTRIBUTE,
  GRID_ID_ATTRIBUTE,
  POLYGON_ID_ATTRIBUTE
} from "@/components/analysisSummary/filterByGrid";
import { setSelectedFeature } from "@/store/slice/mapSlice";
import { SelectedFeature } from "@/types/map";

export const useFeatureSelection = (map: Map) => {
  const dispatch = useDispatch();

  const handleClearSelectedState = (
    currentSelectedFeature: SelectedFeature
  ) => {
    if (!map) return;

    if (currentSelectedFeature?.id === 0 || !!currentSelectedFeature?.id) {
      if (map.getSource(currentSelectedFeature.source)) {
        map.setFeatureState(
          {
            source: currentSelectedFeature.source,
            id: currentSelectedFeature.id
          },
          { selected: false }
        );
      }
      dispatch(setSelectedFeature(null));
    }
  };

  /**
   * Highlight multiple grids by their Grid ID using Maplibre's feature state.
   *
   * @param sourceId - The ID of the data source in the map.
   * @param gridIds - An array of "Grid ID" values to highlight.
   */
  const handleHighlightGridSelection = (
    sourceId: string,
    gridIds: string[]
  ) => {
    if (!map || !sourceId) return;

    const allSourceIds = Object.keys(map.getStyle().sources);
    const matchedSourceId =
      allSourceIds.find((id) => id === sourceId) ??
      allSourceIds.find(
        (id) => id.startsWith(sourceId) && !id.endsWith("-centroid")
      );
    if (!matchedSourceId) return;

    const features = map.querySourceFeatures(matchedSourceId);

    features.forEach((feature) => {
      const properties = feature.properties || {};
      const featureId = properties["unique_id"];
      if (featureId == null) return;
      const id =
        properties[GRID_ID_ATTRIBUTE] ??
        properties[CATCHMENT_ID_ATTRIBUTE] ??
        properties[POLYGON_ID_ATTRIBUTE];
      map.setFeatureState(
        { source: matchedSourceId, id: featureId },
        { selected: gridIds.includes(id) }
      );
    });
  };

  const handleClearHighlightedGrids = (sourceId: string | null) => {
    if (!map || !sourceId) return;
    const allSourceIds = Object.keys(map.getStyle().sources);
    const matchedSourceId =
      allSourceIds.find((id) => id === sourceId) ??
      allSourceIds.find(
        (id) => id.startsWith(sourceId) && !id.endsWith("-centroid")
      );

    if (!matchedSourceId) return;

    const features = map.querySourceFeatures(matchedSourceId);

    features.forEach((feature) => {
      const featureId = feature.properties?.["unique_id"];
      if (featureId == null) return;
      map.setFeatureState(
        { source: matchedSourceId, id: featureId },
        { selected: false }
      );
    });
  };

  return {
    handleClearSelectedState,
    handleHighlightGridSelection,
    handleClearHighlightedGrids
  };
};

export default useFeatureSelection;
